// A random per-browser secret that identifies the guest profile. Storage can be
// blocked inside embeds/in-app browsers, so fall back to an in-memory token.
let mem: string | null = null;

// A guest arriving from the old address brings their token along (see moveScript on the server).
const moved = new URLSearchParams(location.hash.slice(1)).get("t");
if (moved) {
  history.replaceState(null, "", location.pathname + location.search);
  try {
    if (!localStorage.getItem("xrooms_token") && moved.length >= 16) localStorage.setItem("xrooms_token", moved.slice(0, 200));
  } catch {}
}

export function getToken(): string {
  try {
    let t = localStorage.getItem("xrooms_token");
    if (!t) {
      t = crypto.randomUUID() + crypto.randomUUID();
      localStorage.setItem("xrooms_token", t);
    }
    return t;
  } catch {
    return (mem ??= crypto.randomUUID() + crypto.randomUUID());
  }
}

/**
 * Inside an X post the game runs in a frame on x.com, and browsers don't send our sign-in cookie there.
 * So the game keeps a session of its own, got through a pop-up (see handoffPage on the server).
 */
export const inFrame = (() => {
  try {
    return window.top !== window;
  } catch {
    return true;
  }
})();
let memSid = "";
export function getSession(): string {
  if (!inFrame) return "";
  try {
    return localStorage.getItem("xrooms_sid") ?? "";
  } catch {
    return memSid;
  }
}
function setSession(sid: string) {
  memSid = sid;
  try {
    if (sid) localStorage.setItem("xrooms_sid", sid);
    else localStorage.removeItem("xrooms_sid");
  } catch {}
}
/** Headers that carry the frame's session, if it has one. */
export const authHeaders = (): Record<string, string> => (getSession() ? { authorization: `Bearer ${getSession()}` } : {});

/** Signs in from inside a frame: X's sign-in page can't open in one, so it opens in a pop-up. */
function signInPopup(): Promise<string | null> {
  const w = window.open("/auth/handoff", "xrooms_signin", "popup,width=520,height=720");
  if (!w) return Promise.resolve("The sign-in window was blocked. Allow pop-ups for this post and try again.");
  return new Promise((resolve) => {
    const done = (err: string | null) => {
      removeEventListener("message", on);
      clearInterval(closed);
      resolve(err);
    };
    const on = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.source !== w || e.data?.t !== "xr_signin") return;
      if (!e.data.sid) return done("Couldn't sign in with X. Try again.");
      setSession(String(e.data.sid));
      location.reload();
      done(null);
    };
    addEventListener("message", on);
    const closed = setInterval(() => w.closed && done(null), 500);
  });
}

/**
 * Sends the browser to X to sign in, coming back to this page afterwards. The guest progress of this
 * browser moves into the X account if it's the account's first sign-in. Returns an error to show, if any.
 */
export async function signInWithX(): Promise<string | null> {
  if (inFrame) return signInPopup();
  try {
    const r = await fetch("/api/signin", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token: getToken(), back: location.pathname }),
    });
    if (!r.ok) return r.status === 503 ? await r.text() : "Couldn't reach X. Try again in a moment.";
    location.href = (await r.json()).url;
    return null;
  } catch {
    return "Couldn't reach X. Try again in a moment.";
  }
}

export async function signOut() {
  await fetch("/api/signout", { method: "POST", headers: authHeaders() }).catch(() => {});
  setSession("");
  location.reload();
}
